/**
 * `@septima/feature-core/react`: the web side of a feature gate (ADR-0027 D9).
 *
 * - `FeatureProvider` holds the runtime of the features: the feature files of
 *   the build, the enabled ids, the seat classes of each seat-gated id, the
 *   seats of the user and the active persona.
 * - `useFeature(id)` is true when the deployment runs the feature AND a seat
 *   of the user covers it. `<Feature id>` renders its children on that
 *   condition. An inline gate is a run-time check only: its code still ships.
 * - A **slot** is a named place on a page. Each feature file contributes items
 *   to slots (`slots` of `defineWebFeature`). A page augments `SlotMap` with
 *   the type of each of its slots, and reads them with `useSlot` (cards,
 *   tabs, actions, columns) or `useSlotData` (data sources).
 * - A feature file also contributes routes, sidebar rows and other lists. The
 *   shell reads them from `WEB_FEATURES`.
 *
 * Only web code imports this entry. React and TanStack Query are optional
 * peer dependencies: the root entry stays free of both.
 */
import {
    Fragment,
    createContext,
    createElement,
    useContext,
    useMemo,
    type ComponentType,
    type ReactNode,
} from 'react';
import { useQueries, type UseQueryOptions } from '@tanstack/react-query';

import type { FeatureId, SeatKey } from './catalogue';

// ---------------------------------------------------------------------------
// Contributions
// ---------------------------------------------------------------------------

/**
 * The registry of the slot types. A page augments it next to the page:
 *
 * ```ts
 * declare module '@septima/feature-core/react' {
 *     interface SlotMap {
 *         'desk.dashboard.cards': CardContribution<DeskDashboardCtx>;
 *     }
 * }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface SlotMap {}

export type SlotName = keyof SlotMap & string;

/** The fields of each slot item. */
export interface ContributionBase<C> {
    /** Stable: the React key, the test-id seed and the identity of the item. */
    readonly key: string;
    /** The place in the slot. A lower number comes first. A tie sorts by `key`. */
    readonly order: number;
    /** The personas that see the item. Omitted: each persona that the page admits. */
    readonly personas?: readonly string[];
    /** A visibility rule on the page context (for example "the client has an account"). */
    readonly when?: (ctx: C) => boolean;
}

/** The widths of a card in the 12-column panel grid. */
export type CardSpan = 3 | 4 | 6 | 8 | 12;

/**
 * The height of a card, in rows of the panel grid. A number is a fixed count of rows.
 * `'fit'` is for a table: the page turns the row count that the card reports (`fitRows`)
 * into a height.
 */
export type CardRows = number | 'fit';

/** What a card does when it has no actionable item. */
export type CardEmptyBehaviour = 'collapse' | 'keep';

export interface CardContribution<C> extends ContributionBase<C> {
    readonly span: CardSpan;
    readonly Component: ComponentType<{ ctx: C }>;
    /**
     * The name of the card, for the module list of an editable layout (the hidden list,
     * the add-module list). Default: the key.
     */
    readonly title?: string;
    /** One sentence on what the card shows, for the module list of an editable layout. */
    readonly summary?: string;
    /** The default height in 48px grid rows, or `'fit'` for a table. Default: 2. */
    readonly rows?: CardRows;
    /** The widths that the card accepts. Default: each `CardSpan`. */
    readonly spans?: readonly CardSpan[];
    /**
     * The count of actionable items in the card. It drives the collapse and the hidden cue.
     * `null` means that the page cannot know the count (for example, the data hook runs
     * inside the card). Such a card never collapses and never raises the hidden cue.
     */
    readonly count?: (ctx: C) => number | null;
    /**
     * `'collapse'`: a card with a count of zero shows its header row only.
     * `'keep'`: the card keeps its full height. Default: `'keep'`.
     */
    readonly emptyBehaviour?: CardEmptyBehaviour;
    /** True: a new user gets the card in the hidden list and not on the page. */
    readonly defaultHidden?: boolean;
    /** For `rows: 'fit'`: the number of rows of the table, so the height needs no measurement. */
    readonly fitRows?: (ctx: C) => number;
    /**
     * The page of the card. The collapsed header and the hidden cue link to this page
     * with "View all". Omitted: the card has no such link.
     */
    readonly href?: string;
    /**
     * The text of the count in a collapsed header, for example "0 due".
     * Default: the count alone.
     */
    readonly countLabel?: (count: number) => string;
}

export interface TabContribution<C> extends ContributionBase<C> {
    readonly label: string;
    readonly Component: ComponentType<{ ctx: C }>;
}

export interface ActionContribution<C> extends ContributionBase<C> {
    readonly Component: ComponentType<{ ctx: C }>;
}

/** A table column. `Col` is the column type of the table kit of the app. */
export interface ColumnContribution<C, Col> extends ContributionBase<C> {
    readonly column: (ctx: C) => Col;
}

/**
 * A data source. A page reads a variable number of sources with one call to
 * `useSlotData`, so the rules of hooks hold. A source gets its input in one
 * of three ways:
 * - `query`: a TanStack query (`useQueries` runs each one);
 * - `useInput`: a hook. `useSlotData` calls the hook of EACH source of the
 *   slot in the build, also of a source that a seat, a persona or `when`
 *   hides, so the number of hook calls never changes;
 * - neither: the source reads the page context only.
 * `select` makes the value from the input and the context.
 */
export interface DataContribution<C, T> extends ContributionBase<C> {
    // The query option types of TanStack are invariant in their data type, so
    // a slot of mixed sources keeps the options loose. `select` types the result.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly query?: (ctx: C) => UseQueryOptions<any, any, any, any>;
    readonly useInput?: (ctx: C) => unknown;
    readonly select: (input: unknown, ctx: C) => T;
}

/** The value type of a data slot. */
export type SlotValue<S extends SlotName> =
    SlotMap[S] extends DataContribution<never, infer T> ? T : never;

/** One resolved item of a data slot. */
export interface SlotData<S extends SlotName> {
    readonly key: string;
    readonly order: number;
    readonly value: SlotValue<S>;
    /** True while the query of the source has no data. */
    readonly pending: boolean;
    /** True when the query of the source failed. */
    readonly error: boolean;
}

/** A route of a feature. The shell turns it into a route of its router. */
export interface RouteContribution {
    /** The layout that holds the route: a staff view, `'portal'` or `'public'` (the root). */
    readonly frontend: string;
    /** The path under the layout. Use `''` for the index route of the layout. */
    readonly path: string;
    /** The page module. The shell wraps it in `lazy` and `Suspense`. */
    readonly lazy?: () => Promise<{ default: ComponentType }>;
    /** A ready element, for a page that must not wait for a chunk, or a redirect. */
    readonly element?: ReactNode;
}

/** A sidebar row of a staff view. */
export interface NavContribution {
    readonly frontend: string;
    readonly key: string;
    readonly label: string;
    readonly href: string;
    readonly icon: string;
    /** The id of the sidebar group of the view. */
    readonly group: string;
    /** The place in the section table of the view, and in the group. */
    readonly order: number;
    /** Other routes of the same row (a renamed href that still works). */
    readonly aliases?: readonly string[];
    /** Retired: off the sidebar AND the route refuses each persona. */
    readonly hidden?: boolean;
    /** Off the sidebar only: the route stays open. */
    readonly sidebarHidden?: boolean;
    /** Not finished: the row goes to the "Work in Progress" group. */
    readonly wip?: boolean;
}

/** The contributions of one feature file. */
export interface WebFeature {
    readonly id: FeatureId;
    readonly routes?: readonly RouteContribution[];
    readonly nav?: readonly NavContribution[];
    readonly slots?: { readonly [S in SlotName]?: readonly SlotMap[S][] };
}

/**
 * Declares the contributions of one feature. The Vite plugin reads the id
 * from the source, so `id` must be the FIRST property, as a string literal,
 * and the file must hold one feature.
 */
export function defineWebFeature<const F extends WebFeature>(feature: F): F {
    return feature;
}

// ---------------------------------------------------------------------------
// The runtime
// ---------------------------------------------------------------------------

/** What the provider knows. The app builds it from `virtual:septima-features` and the session. */
export interface FeatureRuntime {
    /** The feature files of the build. The list must not change while the provider is mounted. */
    readonly features: readonly WebFeature[];
    /** The enabled ids of the deployment. */
    readonly enabled: ReadonlySet<string>;
    /** The seat classes of each seat-gated id. A user needs one of them. */
    readonly seatOf: ReadonlyMap<string, readonly SeatKey[]>;
    /** True when a bought package grants every seat to every user. */
    readonly grantsAllSeats: boolean;
    /** The seats of the signed-in user (from the session). */
    readonly seats: readonly SeatKey[];
    /** The active persona, or null before the session loads. */
    readonly persona: string | null;
}

const EMPTY_RUNTIME: FeatureRuntime = {
    features: [],
    enabled: new Set(),
    seatOf: new Map(),
    grantsAllSeats: false,
    seats: [],
    persona: null,
};

const FeatureContext = createContext<FeatureRuntime>(EMPTY_RUNTIME);

/** True when the deployment runs the feature and a seat of the user covers it. */
export function isFeatureOn(runtime: FeatureRuntime, id: string): boolean {
    if (!runtime.enabled.has(id)) return false;
    const seats = runtime.seatOf.get(id);
    if (!seats || seats.length === 0 || runtime.grantsAllSeats) return true;
    return seats.some((s) => runtime.seats.includes(s));
}

export interface FeatureProviderProps extends FeatureRuntime {
    readonly children?: ReactNode;
}

/**
 * Provides the feature runtime. The feature list keys the subtree: a
 * different list mounts the subtree again, because `useSlotData` calls the
 * `useInput` hook of each source of the list.
 */
export function FeatureProvider({ children, ...runtime }: FeatureProviderProps): ReactNode {
    const { features, enabled, seatOf, grantsAllSeats, seats, persona } = runtime;
    const value = useMemo<FeatureRuntime>(
        () => ({ features, enabled, seatOf, grantsAllSeats, seats, persona }),
        [features, enabled, seatOf, grantsAllSeats, seats, persona],
    );
    const listKey = features.map((f) => f.id).join('|');
    return createElement(
        FeatureContext.Provider,
        { value },
        createElement(Fragment, { key: listKey }, children),
    );
}

/** The runtime of the nearest provider. */
export function useFeatureRuntime(): FeatureRuntime {
    return useContext(FeatureContext);
}

/** True when the deployment runs the feature AND a seat of the user covers it. */
export function useFeature(id: FeatureId): boolean {
    return isFeatureOn(useContext(FeatureContext), id);
}

export interface FeatureProps {
    readonly id: FeatureId;
    readonly children?: ReactNode;
    readonly fallback?: ReactNode;
}

/** Renders `children` when the feature is on for the user, else `fallback`. Its code still ships. */
export function Feature({ id, children, fallback = null }: FeatureProps): ReactNode {
    return useFeature(id) ? children : fallback;
}

// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------

/**
 * Each item of the slot in the build, in feature order, with no filter. `T`
 * is the item type that the caller reads: `SlotMap[S]`, or the data
 * contribution of a data slot.
 */
function slotCandidates<S extends SlotName, T = SlotMap[S]>(
    runtime: FeatureRuntime,
    name: S,
): { readonly feature: string; readonly item: T }[] {
    const out: { feature: string; item: T }[] = [];
    for (const f of runtime.features) {
        const items = f.slots?.[name] as readonly T[] | undefined;
        for (const item of items ?? []) out.push({ feature: f.id, item });
    }
    return out;
}

type AnyContribution = ContributionBase<unknown>;

function visible(
    runtime: FeatureRuntime,
    feature: string,
    item: AnyContribution,
    ctx: unknown,
): boolean {
    if (!isFeatureOn(runtime, feature)) return false;
    if (item.personas && (runtime.persona === null || !item.personas.includes(runtime.persona)))
        return false;
    return item.when ? item.when(ctx) : true;
}

function byOrder(a: AnyContribution, b: AnyContribution): number {
    return a.order - b.order || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
}

/**
 * The items of a slot that the user sees: the feature is on for the user,
 * the active persona is admitted, and `when(ctx)` is true. Sorted by `order`.
 */
export function useSlot<S extends SlotName>(name: S, ctx?: unknown): readonly SlotMap[S][] {
    const runtime = useContext(FeatureContext);
    return slotCandidates(runtime, name)
        .filter(({ feature, item }) => visible(runtime, feature, item as AnyContribution, ctx))
        .map(({ item }) => item)
        .sort((a, b) => byOrder(a as AnyContribution, b as AnyContribution));
}

/**
 * The values of a data slot that the user sees, sorted by `order`. A source
 * with a `query` runs it through one `useQueries` call. A source with
 * `useInput` gets the value of its hook.
 */
export function useSlotData<S extends SlotName>(name: S, ctx: unknown): readonly SlotData<S>[] {
    const runtime = useContext(FeatureContext);
    const candidates = slotCandidates<S, DataContribution<unknown, SlotValue<S>>>(runtime, name);

    // The hooks of every source of the build, in a fixed order. The list
    // comes from the build, and the provider mounts again when it changes.
    const inputs = new Map<string, unknown>();
    for (const { item } of candidates) {
        if (item.useInput) inputs.set(item.key, item.useInput(ctx));
    }

    const shown = candidates
        .filter(({ feature, item }) => visible(runtime, feature, item, ctx))
        .map(({ item }) => item)
        .sort(byOrder);
    const withQuery = shown.filter((item) => item.query);
    const results = useQueries({ queries: withQuery.map((item) => item.query!(ctx)) });
    const resultOf = new Map(withQuery.map((item, i) => [item.key, results[i]]));

    return shown.map((item) => {
        const result = resultOf.get(item.key);
        const input = result ? result.data : inputs.get(item.key);
        return {
            key: item.key,
            order: item.order,
            value: item.select(input, ctx),
            pending: result ? result.data === undefined : false,
            error: result ? result.isError : false,
        };
    });
}
